/**
 * TypeScript type definitions for the 13-Week Cash Flow applet.
 *
 * These are the frontend-native types. Once Orval generates the API client
 * from the OpenAPI spec, most request/response types will come from
 * `src/generated/models/`. These manual types bridge the gap until then
 * and define UI-only concerns (chart data, slider state, etc.).
 */

// ── Enums ───────────────────────────────────────────────────────────

export type ForecastStatus = 'draft' | 'in_review' | 'approved' | 'superseded';
export type LiquidityStatus = 'GREEN' | 'YELLOW' | 'RED';
export type CovenantStatus = 'COMPLIANT' | 'CAUTION' | 'BREACH';
export type ActionPriority = 'critical' | 'high' | 'medium' | 'low';
export type ActionStatus = 'open' | 'in_progress' | 'escalated' | 'resolved' | 'closed';
export type AgingBucket = 'Current' | '1-30' | '31-60' | '61-90' | 'Over 90';
export type VendorCriticality = 'Red' | 'Yellow' | 'Green';
export type ConfidenceLevel = 'high' | 'medium' | 'low';

// ── Weekly Forecast ─────────────────────────────────────────────────

export interface WeeklyForecastRow {
  week_number: number;
  week_start_date: string;
  week_end_date: string;

  // Receipts
  ar_collections_current: number;
  ar_collections_1_30: number;
  ar_collections_31_60: number;
  total_contractual_receipts: number;
  ar_collections_61_90: number;
  ar_collections_over_90: number;
  revenue_forecast_pipeline: number;
  other_receipts: number;
  total_forecasted_receipts: number;
  total_receipts: number;

  // Committed disbursements
  payroll_gross: number;
  payroll_taxes: number;
  payroll_net: number;
  debt_service_principal: number;
  debt_service_interest: number;
  rent_leases: number;
  income_taxes: number;
  total_committed_disbursements: number;

  // Discretionary disbursements
  ap_payments: number;
  capex: number;
  non_recurring: number;
  total_discretionary_disbursements: number;
  total_disbursements: number;

  // Cash position
  net_cash_flow: number;
  beginning_cash: number;
  ending_cash: number;

  // Facility
  revolver_draw_recommended: number;
  revolver_repayment_recommended: number;
  revolver_outstanding: number;
  available_credit: number;

  // Status
  liquidity_status: LiquidityStatus;
  liquidity_ratio: number;
  covenant_headroom: number;
  covenant_status: CovenantStatus;
  line_item_confidence: Record<string, ConfidenceLevel>;
}

// ── Forecast Version ────────────────────────────────────────────────

export interface ForecastVersion {
  forecast_id: string;
  forecast_version: number;
  created_by: string;
  created_at: string;
  status: ForecastStatus;
  status_changed_at?: string;
  status_changed_by?: string;
  source_load_ids: number[];
  assumption_snapshot: Record<string, any>;
}

// ── Forecast Summary ────────────────────────────────────────────────

export interface ForecastSummary {
  forecast_id: string;
  version: number;
  status: ForecastStatus;
  weeks: WeeklyForecastRow[];
  kpis: ForecastKPIs;
  created_at: string;
  created_by: string;
  retrieved_at?: string;
  data_status?: 'fresh' | 'cached' | 'stale';
  is_cached?: boolean;
  data_warning?: string;
}

export interface ForecastKPIs {
  total_receipts_13w: number;
  total_disbursements_13w: number;
  net_cash_flow_13w: number;
  minimum_cash_week: number;
  minimum_cash_amount: number;
  ending_cash_week13: number;
  average_weekly_burn: number;
  weeks_of_runway: number;
  covenant_breach_week: number | null;
  utilization_peak_pct: number;
}

// ── AR / AP Aging ───────────────────────────────────────────────────

export interface ARAgingRecord {
  id: number;
  entity_name: string;
  customer_name: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  aging_bucket: AgingBucket;
  days_outstanding: number;
  open_balance: number;
  customer_behavior_class?: string;
  dispute_status?: string;
}

export interface APAgingRecord {
  id: number;
  entity_name: string;
  vendor_name: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  open_balance: number;
  payment_terms: string;
  vendor_criticality: VendorCriticality;
  dispute_status?: string;
}

export interface AgingSummary {
  total_ar: number;
  by_bucket: Record<string, number>;
  entity_count: number;
  customer_count: number;
}

// ── Actions ─────────────────────────────────────────────────────────

export interface CashflowAction {
  id: number;
  forecast_id: string;
  action_type: string;
  description: string;
  priority: ActionPriority;
  status: ActionStatus;
  assigned_to?: string;
  due_date?: string;
  cash_impact_estimated?: number;
  cash_impact_realized?: number;
  related_records?: Record<string, any>;
  created_at: string;
  updated_at?: string;
}

export interface ActionSummary {
  total_open: number;
  total_in_progress: number;
  total_escalated: number;
  total_resolved: number;
  total_overdue: number;
  realized_cash_impact: number;
}

// ── Facility ────────────────────────────────────────────────────────

export interface FacilityConfig {
  total_commitment: number;
  current_outstanding: number;
  minimum_cash_covenant: number;
  maximum_utilization_pct: number;
  draw_minimum: number;
  draw_approval_threshold: number;
}

// ── Scenario ────────────────────────────────────────────────────────

export type CapexTiming = 'as_planned' | 'defer_all' | 'accelerate';

export interface ScenarioParams {
  ar_collection_delay_days: number;
  ar_collection_rate: number;
  ap_payment_accel_days: number;
  revenue_pipeline_multiplier: number;
  payroll_adjustment_pct: number;
  capex_timing: CapexTiming;
}

export const DEFAULT_SCENARIO_PARAMS: ScenarioParams = {
  ar_collection_delay_days: 0,
  ar_collection_rate: 100,
  ap_payment_accel_days: 0,
  revenue_pipeline_multiplier: 100,
  payroll_adjustment_pct: 100,
  capex_timing: 'as_planned',
};

export interface ScenarioComputeResult {
  weeks: WeeklyForecastRow[];
  min_cash_week: number;
  min_cash_amount: number;
  ending_cash_week13: number;
  covenant_breach_week: number | null;
}

export interface SavedScenario {
  id: string;
  name: string;
  params: ScenarioParams;
  result: ScenarioComputeResult | null;
}

export interface ScenarioSummaryMetrics {
  name: string;
  min_cash_week: number;
  min_cash_amount: number;
  ending_cash_week13: number;
  breach_status: 'clear' | 'at_risk' | 'breach';
  breach_week: number | null;
  delta_vs_base: number;
}

// ── Assumptions ─────────────────────────────────────────────────────

export interface ForecastAssumption {
  id: number;
  category: string;
  assumption_key: string;
  assumption_value?: number;
  assumption_text?: string;
  description?: string;
  updated_by?: string;
  updated_at?: string;
}

// ── Cash Position ───────────────────────────────────────────────────

export interface CashPositionAccount {
  id: number;
  bank_name: string;
  account_name: string;
  account_type: string;
  current_balance: number;
  available_balance: number;
  is_restricted: boolean;
  as_of_date: string;
}

// ── Variance ────────────────────────────────────────────────────────

export interface VarianceRow {
  week_number: number;
  forecast_receipts: number;
  actual_receipts: number;
  receipt_variance: number;
  receipt_variance_pct: number;
  forecast_disbursements: number;
  actual_disbursements: number;
  disbursement_variance: number;
  disbursement_variance_pct: number;
}

// ── Chart-Specific Types ────────────────────────────────────────────

export interface WaterfallDataPoint {
  name: string;
  value: number;
  fill: string;
  isSubtotal?: boolean;
}

export interface CashFlowChartPoint {
  week: string;
  ending_cash: number;
  receipts: number;
  disbursements: number;
  caution_threshold: number;
  critical_threshold: number;
}

export interface ConfidenceBand {
  week_number: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
}

// ── Data Load ───────────────────────────────────────────────────────

export interface DataLoad {
  id: number;
  load_type: string;
  source_filename?: string;
  validation_status: string;
  record_count?: number;
  load_date: string;
  created_by?: string;
  source?: string;
  total_balance?: number;
  entity_count?: number;
}

// ── Override ────────────────────────────────────────────────────────

export interface AssumptionOverride {
  id: number;
  forecast_id: string;
  line_item: string;
  week_number: number;
  original_value: number;
  override_value: number;
  reason: string;
  changed_by: string;
  changed_at: string;
}

// ── Paginated Response ──────────────────────────────────────────────

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

// ── Applet Data Source Configuration ─────────────────────────────────

export interface AppletDataSourceConfig {
  connector_id: string | null;
  connector_name: string | null;
  connector_type: string | null;
  is_connected: boolean;
  redshift_schema: string | null;
}

export interface ConnectorOption {
  connector_id: string;
  connector_name: string;
  connector_type: string;
  is_healthy: boolean;
  last_health_check: string | null;
}

// ── Feature 1: Availability & Cash Truth ─────────────────────────────

export interface UnclearedAdjustment {
  id: number;
  as_of_date: string;
  account_id: string;
  adjustment_type: string;
  reference_number?: string;
  payee?: string;
  amount: number;
  issue_date?: string;
  expected_clear_date?: string;
  status: string;
  entity_name?: string;
}

export interface AvailabilitySummary {
  id: number;
  as_of_date: string;
  book_cash: number;
  bank_cash: number;
  restricted_cash: number;
  outstanding_checks: number;
  uncleared_deposits: number;
  other_float_adjustments: number;
  true_operating_cash: number;
  available_credit: number;
  total_availability: number;
  covenant_headroom: number;
}

export interface AvailabilityWaterfall {
  current: AvailabilitySummary;
  history: AvailabilitySummary[];
  uncleared_items: UnclearedAdjustment[];
}

// ── Feature 2: Variance Intelligence ─────────────────────────────────

export interface VarianceDetail {
  id: number;
  forecast_id: string;
  week_number: number;
  week_start_date: string;
  line_item: string;
  forecast_amount: number;
  actual_amount: number;
  variance_amount: number;
  variance_pct: number;
  root_cause_category?: string;
  root_cause_detail?: string;
  attributed_entity?: string;
  attributed_counterparty?: string;
  impact_on_ending_cash?: number;
}

export interface VarianceNarrative {
  id: number;
  forecast_id: string;
  week_number: number;
  narrative_type: string;
  narrative_text: string;
  key_drivers?: string;
  recommended_actions?: string;
  model_used?: string;
  generated_at: string;
  approved_by?: string;
}

export interface VarianceIntelligence {
  detail: VarianceDetail[];
  narratives: VarianceNarrative[];
}

// ── Feature 3: Monthly Rollup ────────────────────────────────────────

export interface MonthlyRollup {
  id: number;
  forecast_id: string;
  month_year: string;
  month_start_date: string;
  month_end_date: string;
  weeks_included?: string;
  beginning_cash: number;
  total_receipts: number;
  total_disbursements: number;
  net_cash_flow: number;
  ending_cash: number;
  total_availability?: number;
  covenant_headroom?: number;
  budget_receipts?: number;
  budget_disbursements?: number;
  budget_variance_receipts?: number;
  budget_variance_disbursements?: number;
}

// ── Feature 4: Budget Engine ─────────────────────────────────────────

export interface BudgetFinancial {
  id: number;
  fiscal_year: number;
  period_type: string;
  period_start_date: string;
  period_end_date: string;
  entity_name?: string;
  line_item_category: string;
  line_item_name: string;
  budget_amount: number;
  forecast_amount?: number;
  actual_amount?: number;
  variance_to_budget?: number;
}

export interface CashConversionRule {
  id: number;
  rule_name: string;
  line_item_category: string;
  conversion_type: string;
  timing_profile: string;
  confidence_level?: string;
}

export interface RevenueDetail {
  id: number;
  entity_name: string;
  period_start_date: string;
  period_end_date: string;
  line_of_business?: string;
  revenue_type?: string;
  amount: number;
  daily_run_rate?: number;
}

export interface BudgetEngine {
  financials: BudgetFinancial[];
  conversion_rules: CashConversionRule[];
  revenue: RevenueDetail[];
}

// ── Feature 5: FP&A Feedback ─────────────────────────────────────────

export interface FPAFeedback {
  id: number;
  forecast_id: string;
  feedback_type: string;
  category: string;
  metric_name: string;
  current_assumption?: number;
  recommended_adjustment?: number;
  adjustment_rationale: string;
  cash_impact_weekly?: number;
  cash_impact_13w?: number;
  confidence_level?: string;
  status: string;
  reviewed_by?: string;
  reviewed_at?: string;
  created_at?: string;
}

// ── Feature 6: Working Capital ───────────────────────────────────────

export interface WorkingCapitalRecommendation {
  id: number;
  forecast_id: string;
  recommendation_type: string;
  target_week: number;
  counterparty_name?: string;
  entity_name?: string;
  current_timing?: string;
  recommended_timing?: string;
  cash_impact: number;
  irr_annualized?: number;
  cost_of_capital_comparison?: number;
  priority_rank?: number;
  rationale: string;
  status: string;
  actioned_by?: string;
  actioned_at?: string;
  realized_impact?: number;
}

// ── Feature 7: Drift Monitor ─────────────────────────────────────────

export interface DailyBankBalance {
  id: number;
  balance_date: string;
  account_id: string;
  account_name?: string;
  bank_name?: string;
  ledger_balance: number;
  available_balance?: number;
  inflows_today: number;
  outflows_today: number;
}

export interface DailyBankTotal {
  balance_date: string;
  total_balance: number;
  total_inflows: number;
  total_outflows: number;
}

export interface ForecastDrift {
  id: number;
  drift_date: string;
  forecast_id: string;
  week_number: number;
  day_of_week: number;
  forecast_ending_cash: number;
  actual_bank_total: number;
  drift_amount: number;
  drift_pct: number;
  drift_direction: string;
  severity: string;
  primary_driver?: string;
  requires_intervention: boolean;
}

export interface DriftMonitor {
  drift_records: ForecastDrift[];
  daily_totals: DailyBankTotal[];
  alerts: ForecastDrift[];
}

// ── Feature 8: Cashflow Monitors ─────────────────────────────────────

export type MonitorGroup = 'forecast_accuracy' | 'data_quality' | 'model_drift' | 'agent_health';
export type AlertSeverity = 'critical' | 'warning' | 'info';
export type AlertStatus = 'active' | 'reviewed' | 'remediation_in_progress' | 'snoozed' | 'resolved';

export interface MonitorGroupHealth {
  score: number;
  critical_count: number;
  warning_count: number;
  info_count: number;
  total_active: number;
  status: 'healthy' | 'warning' | 'critical';
}

export interface MonitorSummary {
  groups: Record<MonitorGroup, MonitorGroupHealth>;
  overall_score: number;
  total_critical: number;
  total_warning: number;
  total_active: number;
  overall_status: 'healthy' | 'warning' | 'critical';
}

export interface MonitorAlert {
  id: number;
  customer_id: string;
  monitor_group: MonitorGroup;
  monitor_name: string;
  severity: AlertSeverity;
  status: AlertStatus;
  title: string;
  description?: string;
  observed_value?: number;
  threshold_value?: number;
  primary_driver?: string;
  business_impact?: string;
  business_impact_amount?: number;
  affected_forecasts?: string[];
  recommended_action?: {
    type: string;
    field: string;
    current_value: number;
    suggested_value: number;
    description: string;
  };
  auto_action_id?: number;
  trace_id?: string;
  agent_identifier?: string;
  triggered_at: string;
  reviewed_by?: number;
  reviewed_at?: string;
  snoozed_until?: string;
  resolved_at?: string;
  resolution_notes?: string;
  created_at: string;
}

export interface MonitorAlertList {
  items: MonitorAlert[];
  total: number;
}

export interface MonitorRun {
  id: number;
  agent_identifier: string;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  metrics_snapshot?: Record<string, any>;
  alerts_created: number;
  trace_id?: string;
  error_message?: string;
}

export interface MonitorDefinition {
  id: number;
  monitor_group: MonitorGroup;
  monitor_name: string;
  display_name: string;
  description?: string;
  threshold_config: Record<string, any>;
  schedule_cron?: string;
  is_enabled: boolean;
  agent_identifier: string;
}

export interface AccuracyHistoryRow {
  week_start_date: string;
  line_item: string;
  forecast_amount: number;
  actual_amount: number;
  variance_amount: number;
  absolute_error_pct: number;
  rolling_8w_mape?: number;
}

export interface AccuracyTrendRow {
  line_item: string;
  avg_mape: number;
  latest_rolling_mape?: number;
  weeks_measured: number;
}

export interface AccuracyData {
  history: AccuracyHistoryRow[];
  trend: AccuracyTrendRow[];
}

export interface DriftDataSummary {
  behavior_classes: any[];
  bucket_recovery: any[];
  drifting_classes: any[];
  bucket_drift: any[];
}

export interface MonitorNotification {
  id: number;
  alert_id?: number;
  title: string;
  message?: string;
  severity?: string;
  action_url?: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
}
